import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Send, Plus, Minus, Trash2, Sparkles, AlertCircle, CheckCircle2, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { INGREDIENT_GROUPS } from "@/constants/ingredients";
import PopsicleSVG from "@/components/PopsicleSVG";

const STEP_LABELS = [
  "Name your creation",
  "Choose base category",
  "Add your ingredients",
  "Mix the ratios",
  "Tell your story",
  "Manufacturing notes",
  "Add inspiration",
  "Submit to the lab",
];

const CATEGORY_OPTIONS = [
  { key: "water", label: "Water-Based", blurb: "Refreshing, light, summer." },
  { key: "milk", label: "Milk-Based", blurb: "Comforting, creamy, classic." },
  { key: "creamy", label: "Premium Creamy", blurb: "Rich, indulgent, slow." },
  { key: "zero_sugar", label: "Reduced Sugar", blurb: "Honest, fresh, modern." },
  { key: "other", label: "Other", blurb: "Something else entirely." },
];

const RANDOM_COLORS = ["#FF4F5A","#FFC93C","#7DD3C0","#E27D9C","#A07F58","#74C0FC","#D6B85A","#5B3924"];
const randomColor = () => RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];

const initial = {
  pop_name: "",
  tagline: "",
  category: "",
  ingredients: [],   // { name, percent, group, color }
  taste_profile: "",
  texture: "",
  target_audience: "",
  unique_features: "",
  description: "",
  why_produce: "",
  manufacturing_notes: "",
  inspiration_url: "",
  customer_name: "",
  customer_phone: "",
  customer_email: "",
};

function validateRecipe(data) {
  const total = data.ingredients.reduce((a, b) => a + b.percent, 0);
  const errors = [];
  const tips = [];
  if (data.ingredients.length === 0) errors.push("Add at least one ingredient.");
  if (data.ingredients.length > 0 && total !== 100) errors.push(`Ratios must total 100% (currently ${total}%).`);
  const groups = new Set(data.ingredients.map((i) => i.group));
  if (data.category === "water" && groups.has("dairy")) tips.push("Heads-up — you've mixed dairy into a water-based pop. We may reclassify it as milk-based.");
  if (data.category === "zero_sugar" && data.ingredients.some((i) => ["Chocolate","White Chocolate","Caramel","Butterscotch","Condensed Milk","Sugar"].some(k => i.name.toLowerCase().includes(k.toLowerCase())))) {
    tips.push("Tip: some ingredients are naturally high-sugar. Consider lower percentages or alternatives.");
  }
  if (data.ingredients.length > 8) tips.push("8+ ingredients is a lot. The cleanest pops usually have 3–5.");
  const dominant = data.ingredients.reduce((a, b) => (a.percent > b.percent ? a : b), { percent: 0 });
  if (dominant.percent && dominant.percent < 30 && data.ingredients.length > 0) tips.push("No ingredient dominates — your pop may taste muddled. Try giving one ingredient at least 40%.");
  return { total, errors, tips };
}

export default function FlavourCreator() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [done, setDone] = useState(null);
  const [openGroup, setOpenGroup] = useState("fruit");
  const [customName, setCustomName] = useState("");

  const validation = useMemo(() => validateRecipe(data), [data]);
  const primaryColor = data.ingredients[0]?.color || "#FF4F5A";
  const secondaryColor = data.ingredients[1]?.color || "#FFC93C";

  const addIngredient = (name, group = "custom", color = null) => {
    name = name.trim();
    if (!name) return;
    if (data.ingredients.find((i) => i.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Already added");
      return;
    }
    const c = color || randomColor();
    const next = [...data.ingredients, { name, group, color: c, percent: 0 }];
    // distribute evenly when adding
    const even = Math.floor(100 / next.length);
    const remainder = 100 - even * next.length;
    next.forEach((i, idx) => { i.percent = even + (idx === 0 ? remainder : 0); });
    setData({ ...data, ingredients: next });
  };

  const removeIngredient = (name) => {
    setData({ ...data, ingredients: data.ingredients.filter((i) => i.name !== name) });
  };

  const toggleSuggestion = (name, group, color) => {
    const exists = data.ingredients.find((i) => i.name === name);
    if (exists) removeIngredient(name);
    else addIngredient(name, group, color);
  };

  const setRatio = (name, percent) => {
    percent = Math.max(0, Math.min(100, Number(percent) || 0));
    setData({ ...data, ingredients: data.ingredients.map((i) => i.name === name ? { ...i, percent } : i) });
  };

  const autoBalance = () => {
    if (data.ingredients.length === 0) return;
    const even = Math.floor(100 / data.ingredients.length);
    const remainder = 100 - even * data.ingredients.length;
    setData({ ...data, ingredients: data.ingredients.map((i, idx) => ({ ...i, percent: even + (idx === 0 ? remainder : 0) })) });
  };

  const canNext = () => {
    switch (step) {
      case 0: return data.pop_name.trim().length > 1;
      case 1: return !!data.category;
      case 2: return data.ingredients.length > 0;
      case 3: return validation.errors.length === 0;
      case 4: return data.taste_profile.trim() && data.description.trim();
      case 7: return data.customer_name.trim() && data.customer_phone.trim();
      default: return true;
    }
  };

  const submit = async () => {
    try {
      const payload = {
        pop_name: data.pop_name, tagline: data.tagline, category: data.category,
        flavors: data.ingredients.map((i) => i.name),
        ingredients: data.ingredients.map((i) => ({ name: i.name, percent: i.percent, group: i.group })),
        taste_profile: data.taste_profile, target_audience: data.target_audience,
        unique_features: data.unique_features, description: data.description, why_produce: data.why_produce,
        manufacturing_notes: data.manufacturing_notes, inspiration_url: data.inspiration_url,
        customer_name: data.customer_name, customer_phone: data.customer_phone, customer_email: data.customer_email || "",
        color_primary: primaryColor, color_secondary: secondaryColor,
        sweetness: "medium", addons: [], shape: "classic", packaging: "minimal",
      };
      const { data: r } = await api.post("/dream-pops", payload);
      setDone(r.id);
      toast.success("Your invention is now in our lab!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (done) {
    return (
      <div data-testid="creator-done" className="section-pad">
        <div className="container-pp text-center max-w-2xl mx-auto">
          <div className="grid place-items-center"><PopsicleSVG primary={primaryColor} secondary={secondaryColor} className="h-56" /></div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red mt-6">Inventor #{done.slice(0,6)}</div>
          <h1 className="editorial-h1 text-5xl md:text-6xl mt-3">"{data.pop_name}" is in the lab.</h1>
          <p className="mt-6 text-brand-charcoal/70">Our R&D team reviews every submission. You'll see your idea move through the Innovation Journey on our Hall of Fame — and we may reach out at {data.customer_phone}.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="/hall-of-fame" className="btn-pill btn-dark text-sm" data-testid="creator-view-hof">See Hall of Fame</a>
            <button data-testid="creator-restart" className="btn-pill btn-outline-dark text-sm" onClick={() => { setDone(null); setData(initial); setStep(0); }}>Invent another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="creator-page" className="section-pad">
      <div className="container-pp">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-red"><Sparkles size={14}/> Become a Pop Panda Inventor</div>
          <h1 className="editorial-h1 text-5xl md:text-7xl mt-3">Your Idea Could Become Our Next Bestseller.</h1>
          <p className="mt-6 text-brand-charcoal/70 leading-relaxed">Millions enjoy popsicles. Few get to invent them. This is an open lab — type any ingredient you can imagine, mix your own ratios, and submit your invention to our R&D wall.</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          {/* Sticky preview */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
            <div className="card-soft p-8 grain-overlay">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Live preview</div>
              <div className="aspect-square mt-4 grid place-items-center relative">
                <div className="absolute inset-4 rounded-full" style={{ background: `radial-gradient(circle, ${primaryColor}33, transparent 70%)` }} />
                <PopsicleSVG primary={primaryColor} secondary={secondaryColor} className="h-[80%]" />
                {data.ingredients.slice(0, 5).map((i, idx) => (
                  <span key={i.name} className="absolute text-xs card-soft px-3 py-1.5 animate-float-y"
                    style={{
                      top: `${10 + idx * 16}%`,
                      left: idx % 2 === 0 ? "-4%" : "auto",
                      right: idx % 2 === 0 ? "auto" : "-4%",
                      animationDelay: `${idx * 0.6}s`,
                    }}>
                    <span className="w-2 h-2 rounded-full inline-block mr-1.5" style={{ background: i.color }} />{i.name}
                  </span>
                ))}
              </div>
              <div className="mt-6">
                <div className="font-display text-3xl">{data.pop_name || "Your Invention"}</div>
                {data.tagline && <div className="text-sm text-brand-charcoal/65 italic mt-1">"{data.tagline}"</div>}
                <div className="text-xs uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">
                  {CATEGORY_OPTIONS.find((c) => c.key === data.category)?.label || "Pick a category"}
                </div>
              </div>
              {data.ingredients.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 mb-2">Recipe</div>
                  <div className="flex h-3 w-full rounded-full overflow-hidden border border-brand-line">
                    {data.ingredients.map((i) => (<div key={i.name} style={{ width: `${i.percent}%`, background: i.color }} title={`${i.name} ${i.percent}%`} />))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {data.ingredients.map((i) => (
                      <span key={i.name} className="text-[10px] tracking-[0.05em] border border-brand-line rounded-full px-2 py-0.5">
                        <span className="w-2 h-2 rounded-full inline-block mr-1" style={{ background: i.color }} />{i.name} {i.percent}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2">
              {validation.errors.map((e, i) => (<div key={i} className="flex items-start gap-2 text-xs text-brand-red bg-brand-red/5 border border-brand-red/20 rounded-2xl p-3"><AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{e}</span></div>))}
              {validation.tips.map((t, i) => (<div key={i} className="flex items-start gap-2 text-xs text-brand-charcoal/75 bg-brand-yellow/15 border border-brand-yellow/30 rounded-2xl p-3"><Lightbulb size={14} className="mt-0.5 shrink-0 text-brand-charcoal" /><span>{t}</span></div>))}
              {validation.errors.length === 0 && data.ingredients.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-2xl p-3"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /><span>Recipe is balanced ({validation.total}%).</span></div>
              )}
            </div>
          </aside>

          {/* Steps */}
          <section className="lg:col-span-7">
            <div className="flex items-center gap-2 text-xs text-brand-charcoal/65">
              <span data-testid="creator-step-counter">Step {step + 1} of {STEP_LABELS.length}</span>
              <span className="opacity-50">•</span>
              <span>{STEP_LABELS[step]}</span>
            </div>
            <div className="mt-3 h-1 w-full bg-brand-muted rounded-full overflow-hidden">
              <div className="h-full bg-brand-charcoal transition-all" style={{ width: `${((step + 1) / STEP_LABELS.length) * 100}%` }} />
            </div>

            <div className="mt-10 min-h-[320px]">
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                  {step === 0 && (
                    <div data-testid="creator-step-name" className="space-y-5 max-w-md">
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Flavour Name</label><input data-testid="cr-name" className="pp-input font-display text-3xl" placeholder="e.g. Monsoon Mango Magic" value={data.pop_name} onChange={(e) => setData({ ...data, pop_name: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Tagline (Optional)</label><input data-testid="cr-tag" className="pp-input" placeholder="A pop that tastes like the first rain." value={data.tagline} onChange={(e) => setData({ ...data, tagline: e.target.value })} /></div>
                    </div>
                  )}

                  {step === 1 && (
                    <div data-testid="creator-step-cat" className="grid sm:grid-cols-2 gap-3">
                      {CATEGORY_OPTIONS.map((c) => (
                        <button key={c.key} data-testid={`cr-cat-${c.key}`} onClick={() => setData({ ...data, category: c.key })} className={`text-left p-5 rounded-3xl border ${data.category === c.key ? "border-brand-charcoal bg-white" : "border-brand-line bg-white/60 hover:bg-white"}`}>
                          <div className="font-display text-2xl">{c.label}</div>
                          <div className="text-xs text-brand-charcoal/65 mt-1">{c.blurb}</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {step === 2 && (
                    <div data-testid="creator-step-ing" className="space-y-6">
                      {/* Custom add */}
                      <div className="card-soft p-5">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Open Innovation — type any ingredient</div>
                        <form onSubmit={(e) => { e.preventDefault(); addIngredient(customName); setCustomName(""); }} className="mt-3 flex gap-2">
                          <input data-testid="cr-custom-input" className="pp-input flex-1" placeholder="e.g. Roasted Filter Coffee, Curry Leaf Oil, Smoked Honey…" value={customName} onChange={(e) => setCustomName(e.target.value)} />
                          <button type="submit" data-testid="cr-custom-add" className="btn-pill btn-dark text-xs"><Plus size={14} /> Add Ingredient</button>
                        </form>
                      </div>

                      {/* Suggestions */}
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-3">Need inspiration? Browse our shelf</div>
                        <div className="flex flex-wrap gap-2 mb-5">
                          {INGREDIENT_GROUPS.map((g) => (
                            <button key={g.key} data-testid={`cr-grp-${g.key}`} onClick={() => setOpenGroup(g.key)} className={`btn-pill text-xs ${openGroup === g.key ? "btn-dark" : "btn-ghost border border-brand-line"}`}>
                              <span className="w-2 h-2 rounded-full inline-block" style={{ background: g.color }} /> {g.label}
                            </button>
                          ))}
                        </div>
                        {INGREDIENT_GROUPS.filter((g) => g.key === openGroup).map((g) => (
                          <div key={g.key} className="flex flex-wrap gap-2">
                            {g.items.map((n) => {
                              const active = data.ingredients.some((i) => i.name === n);
                              return (
                                <button key={n} data-testid={`cr-ing-${n}`} onClick={() => toggleSuggestion(n, g.key, g.color)} className={`btn-pill text-xs ${active ? "btn-primary" : "btn-ghost border border-brand-line"}`}>
                                  {active && <Check size={12} />} {n}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>

                      {/* Selected list */}
                      {data.ingredients.length > 0 && (
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">Your ingredients ({data.ingredients.length})</div>
                          <div className="flex flex-wrap gap-2">
                            {data.ingredients.map((i) => (
                              <span key={i.name} className="text-xs border border-brand-line rounded-full px-3 py-1 flex items-center gap-2" data-testid={`cr-chip-${i.name}`}>
                                <span className="w-2 h-2 rounded-full inline-block" style={{ background: i.color }} />
                                {i.name}
                                <button onClick={() => removeIngredient(i.name)} className="text-brand-red hover:opacity-70"><Trash2 size={11} /></button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <div data-testid="creator-step-ratio" className="space-y-4">
                      {data.ingredients.length === 0 && <div className="text-brand-charcoal/60">Go back and pick at least one ingredient.</div>}
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-brand-charcoal/70">Total: <strong className={validation.total === 100 ? "text-emerald-700" : "text-brand-red"}>{validation.total}%</strong></div>
                        <button onClick={autoBalance} data-testid="cr-balance" className="btn-pill btn-ghost text-xs border border-brand-line">Auto-balance to 100%</button>
                      </div>
                      {data.ingredients.map((i) => (
                        <div key={i.name} className="card-soft p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full inline-block" style={{ background: i.color }} /><div className="font-medium">{i.name}</div></div>
                            <div className="flex items-center gap-2">
                              <button data-testid={`cr-r-minus-${i.name}`} onClick={() => setRatio(i.name, i.percent - 5)} className="p-1.5 rounded-full border border-brand-line"><Minus size={12} /></button>
                              <input data-testid={`cr-r-input-${i.name}`} type="number" min="0" max="100" value={i.percent} onChange={(e) => setRatio(i.name, e.target.value)} className="w-16 text-center border border-brand-line rounded-full px-2 py-1 text-sm" />
                              <span className="text-xs text-brand-charcoal/55">%</span>
                              <button data-testid={`cr-r-plus-${i.name}`} onClick={() => setRatio(i.name, i.percent + 5)} className="p-1.5 rounded-full border border-brand-line"><Plus size={12} /></button>
                              <button onClick={() => removeIngredient(i.name)} className="p-1.5 rounded-full hover:bg-brand-red/10 text-brand-red"><Trash2 size={12} /></button>
                            </div>
                          </div>
                          <input data-testid={`cr-r-slider-${i.name}`} type="range" min="0" max="100" value={i.percent} onChange={(e) => setRatio(i.name, e.target.value)} className="w-full mt-3 accent-brand-red" />
                        </div>
                      ))}
                    </div>
                  )}

                  {step === 4 && (
                    <div data-testid="creator-step-story" className="grid gap-5">
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Taste Profile</label><input data-testid="cr-taste" className="pp-input" placeholder="Sweet, tangy, a hint of cardamom..." value={data.taste_profile} onChange={(e) => setData({ ...data, taste_profile: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Texture</label><input data-testid="cr-texture" className="pp-input" placeholder="Creamy, crunchy, smooth, layered..." value={data.texture} onChange={(e) => setData({ ...data, texture: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Target Audience</label><input data-testid="cr-aud" className="pp-input" placeholder="Who is this pop for?" value={data.target_audience} onChange={(e) => setData({ ...data, target_audience: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Unique Selling Points</label><input data-testid="cr-uniq" className="pp-input" placeholder="What makes it different?" value={data.unique_features} onChange={(e) => setData({ ...data, unique_features: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Product Description</label><textarea data-testid="cr-desc" rows="3" className="pp-input" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Why should Pop Panda make this?</label><textarea data-testid="cr-why" rows="2" className="pp-input" value={data.why_produce} onChange={(e) => setData({ ...data, why_produce: e.target.value })} /></div>
                    </div>
                  )}

                  {step === 5 && (
                    <div data-testid="creator-step-mfg" className="space-y-4 max-w-2xl">
                      <p className="text-sm text-brand-charcoal/70">Optional — but very welcome. If you have ideas about how this pop should be made, share them. We're a lab, not a factory.</p>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Manufacturing notes</label>
                        <textarea data-testid="cr-mfg" rows="6" className="pp-input"
                          placeholder="• Mixing process (e.g. blend mango raw, fold cream after freezing)…
• Layering ideas (e.g. mango core, milk shell, dark chocolate base)…
• Serving concept (eat-frozen, semi-set, soft-serve style)…
• Packaging concept (kraft sleeve, transparent box, festival edition)…
• Presentation ideas (toppings, dipping sauce, garnish)…"
                          value={data.manufacturing_notes} onChange={(e) => setData({ ...data, manufacturing_notes: e.target.value })} />
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div data-testid="creator-step-img" className="max-w-md space-y-4">
                      <p className="text-sm text-brand-charcoal/70">Paste a link to your inspiration — a drawing, a photo, a Pinterest mood board, anything that helps us see your vision.</p>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Inspiration URL (optional)</label><input data-testid="cr-img" className="pp-input" placeholder="https://..." value={data.inspiration_url} onChange={(e) => setData({ ...data, inspiration_url: e.target.value })} /></div>
                    </div>
                  )}

                  {step === 7 && (
                    <div data-testid="creator-step-submit" className="grid sm:grid-cols-2 gap-5 max-w-2xl">
                      <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Your Name</label><input data-testid="cr-cust-name" className="pp-input" value={data.customer_name} onChange={(e) => setData({ ...data, customer_name: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</label><input data-testid="cr-cust-phone" className="pp-input" value={data.customer_phone} onChange={(e) => setData({ ...data, customer_phone: e.target.value })} /></div>
                      <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email (optional)</label><input data-testid="cr-cust-email" className="pp-input" value={data.customer_email} onChange={(e) => setData({ ...data, customer_email: e.target.value })} /></div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="cr-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="btn-pill btn-ghost text-sm disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
              {step < STEP_LABELS.length - 1 ? (
                <button data-testid="cr-next" onClick={() => setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1))} disabled={!canNext()} className="btn-pill btn-dark text-sm disabled:opacity-40">Continue <ArrowRight size={16} className="arrow" /></button>
              ) : (
                <button data-testid="cr-submit" onClick={submit} disabled={!canNext()} className="btn-pill btn-primary text-sm disabled:opacity-40">Submit Invention <Send size={14} /></button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
